import { Markdown } from '@/components/ui'
import type { ReportSection } from '../../stores/types'
import { sectionAnchorId, stripDuplicateTitleHeading } from './headings'
import { ReportChart } from './ReportChart'
import { ReportTable } from './ReportTable'
import { REPORT_PROSE } from './sectionProse'
import { placedArtifactIds, splitMarkdownTokens } from './tokens'

interface ReportSectionBlockProps {
  slug: string
  section: ReportSection
  // Position in the document body ("Section 3"); the hoisted counsel
  // panel carries no number.
  number?: number
  variant?: 'section' | 'counsel'
  // section id -> heading, for recommendation evidence links.
  sectionLinks?: Record<string, string>
}

export function ReportSectionBlock({
  slug,
  section,
  number,
  variant = 'section',
  sectionLinks,
}: ReportSectionBlockProps) {
  const tables = section.tables ?? []
  const charts = section.charts ?? []
  const segments = splitMarkdownTokens(
    stripDuplicateTitleHeading(section.markdown ?? '', section.title),
  )
  const placed = placedArtifactIds(segments)
  const tableById = new Map(tables.map((table) => [table.id ?? '', table]))
  const chartById = new Map(charts.map((chart) => [chart.id ?? '', chart]))
  // The recommendations panel is a ranked checklist read top to bottom,
  // so its table shows every row instead of paginating.
  const paginateTables = variant !== 'counsel'

  const body = (
    <div className="space-y-7">
      {segments.map((segment, index) => {
        if (segment.kind === 'text') {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: segments are static per render
            <Markdown
              key={`text-${index}`}
              content={segment.text}
              componentsOverride={REPORT_PROSE}
            />
          )
        }
        if (segment.kind === 'table') {
          const table = tableById.get(segment.id)
          return table ? (
            <ReportTable
              key={`table-${segment.id}`}
              slug={slug}
              spec={table}
              sectionLinks={sectionLinks}
              paginate={paginateTables}
            />
          ) : null
        }
        const chart = chartById.get(segment.id)
        return chart ? (
          <ReportChart key={`chart-${segment.id}`} spec={chart} />
        ) : null
      })}
      {tables
        .filter((table) => !placed.has(table.id ?? ''))
        .map((table, index) => (
          <ReportTable
            key={table.id ?? `unplaced-table-${index}`}
            slug={slug}
            spec={table}
            sectionLinks={sectionLinks}
            paginate={paginateTables}
          />
        ))}
      {charts
        .filter((chart) => !placed.has(chart.id ?? ''))
        .map((chart, index) => (
          <ReportChart
            key={chart.id ?? `unplaced-chart-${index}`}
            spec={chart}
          />
        ))}
    </div>
  )

  if (variant === 'counsel') {
    return (
      <section
        id={sectionAnchorId(section)}
        className="mt-10 rounded-lg border border-border bg-muted/30 p-6 sm:p-8"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Recommended next steps
        </p>
        <h2 className="mt-2 mb-4 text-pretty text-2xl font-semibold leading-snug tracking-tight text-blue-7">
          {section.title}
        </h2>
        {body}
      </section>
    )
  }

  return (
    <section
      id={sectionAnchorId(section)}
      className="mt-12 border-t border-border pt-10"
    >
      {number !== undefined && (
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Section {number}
        </p>
      )}
      <h2 className="mt-2 mb-4 text-2xl font-semibold leading-snug tracking-tight text-blue-7">
        {section.title}
      </h2>
      {body}
    </section>
  )
}
